import { PolicyApiSdk, ClientInferResponses, contract } from '@pie/policy-api-sdk';
import { useQuery } from '@tanstack/react-query';
import { isFutureIsoDate } from '@upms/dates';
import { env } from '@/config/env';
import { ApiError } from '@/lib/api/ApiError';
import { useAuthContext } from '@/components/AuthContext';

/*
 * Constants
 */
export const POST_ISSUANCE_LIFECYCLE_STATUSES = [
  'active',
  'audited',
  'cancelled',
  'expired',
  'pending-cancellation',
  'reinstated',
] as const;

/*
 * Types
 */
type Policy = Extract<ClientInferResponses<typeof contract.getPolicy>, { status: 200 }>['body'];

export type IssuedPolicy = Omit<Policy, 'lifecycleStatus' | 'policyNumber'> & {
  lifecycleStatus: (typeof POST_ISSUANCE_LIFECYCLE_STATUSES)[number];
  policyNumber: string;
};

const { client } = new PolicyApiSdk({ environment: env.NEXT_PUBLIC_PIE_ENV });

export const usePolicy = (policyId: string) => {
  const { getSessionHeaders } = useAuthContext();

  return useQuery({
    queryFn: async () => {
      const headers = await getSessionHeaders();

      const response = await client.getPolicy({
        headers,
        params: { policyId },
      });

      if (response.status !== 200) {
        throw new ApiError(response.status, response.body, response.headers);
      }

      if (!isIssuedPolicy(response.body)) {
        // TODO: report more detailed answer to Datadog
        throw new Error('This policy is not available to view at this time.');
      }

      return response.body;
    },
    // Callers that read the id from a URL may hold an empty string before one
    // is present; requesting `/policies/` would 404 for a reason unrelated to
    // the policy itself.
    enabled: Boolean(policyId),
    queryKey: ['policyApiSdk', 'getPolicy', policyId],
  });
};

/*
 * Derived
 */

export const getCuratedStatus = (
  policy: Pick<IssuedPolicy, 'effectiveDate' | 'lifecycleStatus'>
) => {
  if (isActivePolicy(policy)) {
    return 'active';
  }

  if (policy.lifecycleStatus === 'active' && isFutureIsoDate(policy.effectiveDate)) {
    return 'pending';
  }

  if (policy.lifecycleStatus === 'cancelled') {
    return 'cancelled';
  }

  if (['audited', 'expired'].includes(policy.lifecycleStatus)) {
    return 'expired';
  }

  throw new Error('Unable to identify the curated status.');
};

export const isActivePolicy = ({
  effectiveDate,
  lifecycleStatus,
}: Pick<IssuedPolicy, 'effectiveDate' | 'lifecycleStatus'>) => {
  return Boolean(
    (lifecycleStatus === 'active' && !isFutureIsoDate(effectiveDate)) ||
    lifecycleStatus === 'pending-cancellation' ||
    lifecycleStatus === 'reinstated'
  );
};

// Keyed off the curated status, not the raw lifecycle status, so the help text
// and the "Canceled" status tag can never disagree about the same policy.
// Accepts undefined so callers can pass an unsettled query straight through:
// a policy we cannot see yet is not a cancelled one.
export const isCancelledPolicy = (
  policy: Pick<IssuedPolicy, 'effectiveDate' | 'lifecycleStatus'> | undefined
) => (policy ? getCuratedStatus(policy) === 'cancelled' : false);

const isIssuedPolicy = (policy: Policy): policy is IssuedPolicy => {
  return (
    POST_ISSUANCE_LIFECYCLE_STATUSES.includes(
      policy.lifecycleStatus as IssuedPolicy['lifecycleStatus']
    ) && policy.policyNumber !== null
  );
};
